import './Field.scss'
import { useId, type ReactNode } from 'react'
import { clx } from '../clx'
import { Spinner } from '../Spinner'
import { useLabelPlacement, type LabelPlacement } from './layout'

/**
 * Props Field hands to its control. Spread them onto the input. Widgets a
 * <label for> cannot name (e.g. a radiogroup) use `${id}-label` with
 * aria-labelledby.
 */
export interface FieldControlProps {
  id: string
  'aria-describedby'?: string
  'aria-invalid'?: true
  'aria-required'?: true
  'aria-busy'?: true
}

export interface FieldProps {
  label: ReactNode
  /** Render prop: receives the ids and ARIA wiring for the control. */
  children: (control: FieldControlProps) => ReactNode
  hint?: ReactNode
  /** Error message. Presence sets aria-invalid on the control. */
  error?: ReactNode
  isRequired?: boolean
  /** An async check (e.g. uniqueness) is running. */
  isBusy?: boolean
  /** Hide the label visually but keep it for assistive technology. */
  isLabelHidden?: boolean
  /** Overrides the placement inherited from FilterBar / FormGrid (default `top`). */
  labelPlacement?: LabelPlacement
  /**
   * `floating` only: whether the control holds a value, so the label stays
   * floated without focus. Unknown (omitted) counts as filled, so the label
   * can never sit on top of a value.
   */
  isFilled?: boolean
  /** Explicit id for the control; generated when omitted. */
  id?: string
  className?: string
}

/**
 * The one label/hint/error wrapper for every form control (SPEC §5.4).
 * Validation messages are linked through aria-describedby.
 */
export function Field({
  label,
  children,
  hint,
  error,
  isRequired = false,
  isBusy = false,
  isLabelHidden = false,
  labelPlacement,
  isFilled = true,
  id,
  className
}: FieldProps) {
  const placement = useLabelPlacement(labelPlacement)
  const generated = useId()
  const controlId = id ?? `field-${generated}`
  const hintId = hint ? `${controlId}-hint` : undefined
  const errorId = error ? `${controlId}-error` : undefined
  const describedBy = [errorId, hintId].filter(Boolean).join(' ') || undefined

  const control: FieldControlProps = {
    id: controlId,
    ...(describedBy && { 'aria-describedby': describedBy }),
    ...(error ? { 'aria-invalid': true as const } : {}),
    ...(isRequired ? { 'aria-required': true as const } : {}),
    ...(isBusy ? { 'aria-busy': true as const } : {})
  }

  const labelElement = (
    <label
      id={`${controlId}-label`}
      htmlFor={controlId}
      className={clx('field__label', isLabelHidden && 'visually-hidden')}
    >
      {label}
      {isRequired && (
        <span className="field__required" aria-hidden="true">
          {' '}
          *
        </span>
      )}
    </label>
  )
  // A floating label is positioned over the control, so it lives inside the
  // control box (still before the control in the DOM).
  const isFloating = placement === 'floating' && !isLabelHidden

  return (
    <div
      className={clx(
        'field',
        placement !== 'top' && `field--as-${placement}`,
        isFloating && isFilled && 'field--is-filled',
        error && 'field--has-error',
        className
      )}
    >
      {!isFloating && labelElement}
      <div className="field__control">
        {isFloating && labelElement}
        {children(control)}
        {isBusy && <Spinner size="sm" className="field__busy" />}
      </div>
      {error && (
        <p id={errorId} className="field__error">
          {error}
        </p>
      )}
      {hint && (
        <p id={hintId} className="field__hint">
          {hint}
        </p>
      )}
    </div>
  )
}
